import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { concat, MonoTypeOperatorFunction, Observable, of, throwError, timer } from 'rxjs';
import { map, reduce } from 'rxjs/operators';
import { catchError, retry } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { ErrorContextService } from 'app/shared/services/error-context/error-context.service';
import { SseClientService, SSEEvent } from './sse-client.service';
import { readClarificationOptions } from './clarification.helper';
import { readProgressFromMetadata } from './message-progress.helper';
import {
  APIChatMessage,
  ChatMessage,
  ChatSession,
  DEOAgentMessageResponse,
  DEOAgentArtifact,
  IResponse,
  HTMLArtifact,
  DEOAgentMessageRequest,
  APIArtifact,
  FeedbackRequest,
  Feedback,
  DEOProject,
  CreateProjectRequest,
  EditProjectRequest,
  SessionRenameRequest,
  SessionProjectRequest,
  ArtifactBulkDeleteResult
} from '../models/chat.models';
import { ProjectInstructionKind, ProjectInstructionSectionRecord, SaveProjectInstructionsRequest } from '../models/project-instruction-section.model';
import { AuthService } from 'app/services/auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class AIAgentService {
  private static readonly SERVICE_UNAVAILABLE_RETRY_COUNT = 2;
  private static readonly SERVICE_UNAVAILABLE_RETRY_DELAY_MS = 2000;
  /** Backend cap on DELETE /artifacts; more ids in one call is a 422 that deletes nothing. */
  private static readonly BULK_DELETE_MAX_IDS = 200;
  private static readonly FEEDBACK_ALREADY_GONE: IResponse<boolean> = { succeeded: true, message: '', data: true };

  private readonly apiUrl = `${environment.backendUrl}/mcp/gxcl/api/claude`;

  constructor(
    private readonly _http: HttpClient,
    private readonly _sseClient: SseClientService,
    private readonly _authService: AuthService,
    private readonly _router: Router,
    private readonly _errorContext: ErrorContextService
  ) {}
  updateFavorite(sessionId: string, isFavorite: boolean): void {
    this.setSessionFavorite(sessionId, isFavorite).subscribe({
      error: () => {
        // Fire-and-forget: the star simply reverts on the next sessions refresh if the update failed.
      }
    });
  }

  /**
   * DATA-1351: 503 means a backend dependency is briefly unavailable (Lakebase/Azure/Databricks or the app
   * still starting), so read-only calls retry a bounded number of times before surfacing the failure.
   * Every other status is not retryable and rethrows immediately.
   */
  private retryWhenServiceUnavailable<T>(): MonoTypeOperatorFunction<T> {
    return retry({
      count: AIAgentService.SERVICE_UNAVAILABLE_RETRY_COUNT,
      delay: (error: unknown, retryCount: number) => {
        if (error instanceof HttpErrorResponse && error.status === 503) {
          return timer(retryCount * AIAgentService.SERVICE_UNAVAILABLE_RETRY_DELAY_MS);
        }
        throw error;
      }
    });
  }
  /**
   * Send a message using SSE streaming
   * Returns an Observable that emits SSE events (heartbeat, complete, error)
   */
  sendMessageStream(sessionId: string | null, message: string, conversationHistory: ChatMessage[], projectId?: string | null): Observable<SSEEvent> {
    const request: DEOAgentMessageRequest = {
      session_id: sessionId,
      message,
      conversation_history: conversationHistory.map(msg => ({
        content: msg.content,
        role: msg.role,
        timestamp: msg.timestamp
      }))
    };
    if (projectId) {
      request.project_id = projectId;
    }

    const url = `${this.apiUrl}/send-message`;
    return this._sseClient.sendMessageWithSSE(url, request);
  }

  /**
   * Send a message and return a complete response
   * Wraps SSE stream into an Observable-based response
   * @param sessionId - Session ID or null for first message (API will create one)
   */
  sendMessage(sessionId: string | null, message: string, conversationHistory: ChatMessage[]): Observable<IResponse<DEOAgentMessageResponse>> {
    return new Observable(observer => {
      this.sendMessageStream(sessionId, message, conversationHistory).subscribe({
        next: event => {
          if (event.type === 'complete') {
            observer.next({
              succeeded: true,
              message: '',
              data: {
                content: event.data.content,
                usage: event.data.usage,
                artifacts: event.data.artifacts ? this.convertHTMLArtifactsToUI(event.data.artifacts, event.data.session_id, event.data.message_id ?? '') : [],
                session_id: event.data.session_id,
                message_id: event.data.message_id ?? null
              }
            });
            observer.complete();
          } else if (event.type === 'error') {
            observer.error(new Error(event.error));
          }
        },
        error: error => {
          observer.error(error);
        }
      });
    });
  }

  /**
   * Convert HTMLArtifact from API response to UI format (temporary IDs until saved).
   * `messageId` is the persisted id of the message that produced them, empty when the backend did not
   * return one (message persistence disabled), since there is no message row to link the artifact to.
   */
  public convertHTMLArtifactsToUI(htmlArtifacts: HTMLArtifact[], sessionId: string, messageId = ''): DEOAgentArtifact[] {
    return htmlArtifacts.map(artifact => ({
      id: crypto.randomUUID(), // Temporary ID - API will assign permanent ID when saved
      type: this.readArtifactType(artifact),
      title: artifact.title,
      htmlContent: artifact.HtmlContent,
      sessionId: sessionId,
      messageId,
      userId: this._authService.getEmail() || 'default_user',
      createdAt: new Date(),
      updatedAt: new Date()
    }));
  }

  /**
   * Reads the artifact type from whichever field the payload carries.
   *
   * Streamed turns name it `type` while the non-streamed reply names it `artifact_type`; the difference
   * only surfaced once DATA-1410 made streamed turns actually deliver their artifacts, and an unread type
   * left the artifact card with a blank badge and the generic icon. Both fields are absent on older
   * payloads, and everything this endpoint produces is an HTML document, so that is the fallback. Stored
   * artifacts come through here for the same reason: not every row carries `artifact_type` (PLAN-2332).
   */
  private readArtifactType(artifact: { artifact_type?: string; type?: string }): string {
    return artifact.artifact_type || artifact.type || 'html';
  }

  /**
   * Convert API artifact response to DEOAgentArtifact UI format
   */
  private convertAPIArtifactToUI(apiArtifact: APIArtifact): DEOAgentArtifact {
    return {
      id: apiArtifact.id,
      type: this.readArtifactType(apiArtifact),
      title: apiArtifact.title,
      htmlContent: apiArtifact.HtmlContent,
      sessionId: apiArtifact.session_id,
      messageId: apiArtifact.message_id,
      userId: apiArtifact.user_id,
      createdAt: new Date(apiArtifact.created_at),
      updatedAt: new Date(apiArtifact.updated_at)
    };
  }

  /**
   * Convert a chat session from the API (snake_case flags) to the UI ChatSession shape.
   * The backend returns is_favorite / is_archived / project_id, which the UI reads as
   * isFavorite / isArchived / projectId.
   *
   * `artifacts` is destructured out of the spread on purpose: only its size is needed, and keeping the raw
   * field would park an artifact payload on every session in the list.
   */
  private convertAPISessionToUI(session: any): ChatSession {
    const { artifacts, ...sessionFields } = session;
    return {
      ...sessionFields,
      isFavorite: session.is_favorite ?? session.isFavorite ?? false,
      isArchived: session.is_archived ?? session.isArchived ?? false,
      projectId: session.project_id ?? session.projectId ?? null,
      artifactCount: this.resolveSessionArtifactCount(session)
    };
  }

  /**
   * How many artifacts a chat owns, straight from the sessions payload (PLAN-2188 / DATA-954).
   *
   * DATA-954 ships the totals as an `artifacts` id list; PLAN-2188 describes the same data as
   * `artifacts_count`. Both are read so the count is right whichever field the deployed backend sends,
   * and a payload carrying neither falls back to zero rather than leaving the card blank.
   */
  private resolveSessionArtifactCount(session: any): number {
    if (Array.isArray(session.artifacts)) {
      return session.artifacts.length;
    }
    return session.artifacts_count ?? session.artifactCount ?? 0;
  }

  getChatSessions(): Observable<IResponse<ChatSession[]>> {
    return this._http.get<IResponse<ChatSession[]>>(`${this.apiUrl}/sessions`).pipe(
      this.retryWhenServiceUnavailable(),
      map(response => ({
        ...response,
        data: response.data ? response.data.map(session => this.convertAPISessionToUI(session)) : []
      }))
    );
  }

  getChatMessages(sessionId: string): Observable<IResponse<ChatMessage[]>> {
    return this._http.get<IResponse<APIChatMessage[]>>(`${this.apiUrl}/sessions/${sessionId}/messages`).pipe(
      this.retryWhenServiceUnavailable(),
      map(response => ({
        ...response,
        data: response.data ? response.data.map(message => this.convertAPIMessageToUI(message)) : response.data
      }))
    );
  }

  private convertAPIMessageToUI(apiMessage: APIChatMessage): ChatMessage {
    const { metadata, ...message } = apiMessage;
    const clarificationOptions = readClarificationOptions(metadata?.clarification, metadata?.options);
    const progressSteps = readProgressFromMetadata(metadata?.activity);
    return {
      ...message,
      ...(clarificationOptions ? { clarificationOptions } : {}),
      ...(progressSteps ? { progressSteps } : {})
    };
  }

  deleteChatSession(sessionId: string): Observable<IResponse<boolean>> {
    return this._http.delete<IResponse<boolean>>(`${this.apiUrl}/sessions/${sessionId}`);
  }

  setSessionFavorite(sessionId: string, isFavorite: boolean): Observable<IResponse<boolean>> {
    return this._http.patch<IResponse<boolean>>(`${this.apiUrl}/sessions/${sessionId}/favorite/${isFavorite}`, null);
  }

  renameSession(sessionId: string, title: string): Observable<IResponse<boolean>> {
    const body: SessionRenameRequest = { title };
    return this._http.patch<IResponse<boolean>>(`${this.apiUrl}/sessions/rename/${sessionId}`, body);
  }

  setSessionProject(sessionId: string, projectId: string | null): Observable<IResponse<boolean>> {
    const body: SessionProjectRequest = { project_id: projectId };
    return this._http.patch<IResponse<boolean>>(`${this.apiUrl}/sessions/${sessionId}/project`, body);
  }

  getSessionArtifacts(sessionId: string): Observable<IResponse<DEOAgentArtifact[]>> {
    return this._http.get<IResponse<APIArtifact[]>>(`${this.apiUrl}/sessions/${sessionId}/artifacts`).pipe(
      this.retryWhenServiceUnavailable(),
      map(response => ({
        ...response,
        data: response.data ? response.data.map((artifact: any) => this.convertAPIArtifactToUI(artifact)) : []
      }))
    );
  }

  getAllArtifacts(): Observable<IResponse<DEOAgentArtifact[]>> {
    return this._http.get<IResponse<APIArtifact[]>>(`${this.apiUrl}/artifacts`).pipe(
      this.retryWhenServiceUnavailable(),
      map(response => ({
        ...response,
        data: response.data ? response.data.map((artifact: any) => this.convertAPIArtifactToUI(artifact)) : []
      })),
      catchError((error: HttpErrorResponse) => {
        this.redirectToErrorPageIfArtifactsUnavailable(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * PLAN-2158: a 200 means the artifact is gone whatever `data` says — `data: false` only reports that
   * nothing matched the id (already deleted, never existed, or owned by another user), which is an
   * idempotent success. Callers must branch on the HTTP status, i.e. on next-vs-error, never on `data`:
   * a failure carries `data: false` too, so reading `data` alone makes an outage look like a delete.
   */
  deleteArtifact(artifactId: string): Observable<IResponse<boolean>> {
    return this._http.delete<IResponse<boolean>>(`${this.apiUrl}/artifacts/${artifactId}`);
  }

  /**
   * PLAN-2158: deletes a batch, chunked to the backend's cap because exceeding it is rejected as a 422
   * with nothing deleted at all. Chunks run sequentially rather than in parallel to stay clear of the
   * 120 requests/minute per-tenant rate limit, and their counts are merged into one result.
   *
   * The ids must travel in the request body, which Angular only sends on DELETE via the `body` option.
   * Getting that wrong is silent: the call returns 200 with `requested: 0` and deletes nothing.
   */
  bulkDeleteArtifact(artifactIds: string[]): Observable<IResponse<ArtifactBulkDeleteResult>> {
    const chunks = this.chunkArtifactIds(artifactIds);

    return concat(...chunks.map(chunk => this.requestBulkDelete(chunk))).pipe(reduce((merged, response) => this.mergeBulkDeleteResults(merged, response)));
  }

  private requestBulkDelete(artifactIds: string[]): Observable<IResponse<ArtifactBulkDeleteResult>> {
    return this._http.delete<IResponse<ArtifactBulkDeleteResult>>(`${this.apiUrl}/artifacts`, { body: { artifact_ids: artifactIds } });
  }

  private chunkArtifactIds(artifactIds: string[]): string[][] {
    const chunks: string[][] = [];

    for (let index = 0; index < artifactIds.length; index += AIAgentService.BULK_DELETE_MAX_IDS) {
      chunks.push(artifactIds.slice(index, index + AIAgentService.BULK_DELETE_MAX_IDS));
    }

    return chunks.length ? chunks : [[]];
  }

  private mergeBulkDeleteResults(merged: IResponse<ArtifactBulkDeleteResult>, response: IResponse<ArtifactBulkDeleteResult>): IResponse<ArtifactBulkDeleteResult> {
    if (!merged?.data || !response?.data) {
      return response;
    }

    return {
      ...response,
      data: {
        requested: merged.data.requested + response.data.requested,
        deleted: merged.data.deleted + response.data.deleted,
        deleted_ids: [...(merged.data.deleted_ids ?? []), ...(response.data.deleted_ids ?? [])],
        not_found_ids: [...(merged.data.not_found_ids ?? []), ...(response.data.not_found_ids ?? [])]
      }
    };
  }

  /**
   * When the artifacts endpoint is missing entirely (404) the artifacts screen cannot render, so send the
   * user to the generic error page. Server failures (5xx) stay on the screen: DATA-1351 gives them a
   * user-friendly message + reference code and 502/503 are transient, so the screen shows the message
   * and offers a retry instead of navigating away.
   */
  private redirectToErrorPageIfArtifactsUnavailable(error: HttpErrorResponse): void {
    if (!this.isArtifactsEndpointMissing(error)) {
      return;
    }
    this._errorContext.recordError(this._router.url);
    this._router.navigate(['/error']);
  }

  private isArtifactsEndpointMissing(error: HttpErrorResponse): boolean {
    return !!error && error.status === 404;
  }

  getProjects(): Observable<IResponse<DEOProject[]>> {
    return this._http.get<IResponse<DEOProject[]>>(`${this.apiUrl}/sessions/projectdetails`).pipe(this.retryWhenServiceUnavailable());
  }

  deleteProject(projectId: string | number, deleteChats: boolean): Observable<IResponse<boolean>> {
    const params = new HttpParams().set('delete_sessions', deleteChats.toString());
    return this._http.delete<IResponse<boolean>>(`${this.apiUrl}/project/${projectId}`, { params });
  }

  getProjectArtifacts(projectId: string | number): Observable<IResponse<DEOAgentArtifact[]>> {
    return this._http.get<IResponse<APIArtifact[]>>(`${this.apiUrl}/sessions/${projectId}/artifacts/projects`).pipe(
      this.retryWhenServiceUnavailable(),
      map(response => ({
        ...response,
        data: response.data ? response.data.map((artifact: APIArtifact) => this.convertAPIArtifactToUI(artifact)) : []
      }))
    );
  }

  getProjectSessions(projectId: string | number): Observable<IResponse<ChatSession[]>> {
    return this._http.get<IResponse<ChatSession[]>>(`${this.apiUrl}/sessions/projectwise/${projectId}`).pipe(
      this.retryWhenServiceUnavailable(),
      map(response => ({
        ...response,
        data: response.data ? response.data.map(session => this.convertAPISessionToUI(session)) : []
      }))
    );
  }

  createProject(projectData: CreateProjectRequest): Observable<IResponse<DEOProject>> {
    return this._http.post<IResponse<DEOProject>>(`${this.apiUrl}/project/add`, projectData);
  }

  editProject(projectData: EditProjectRequest): Observable<IResponse<DEOProject>> {
    return this._http.post<IResponse<DEOProject>>(`${this.apiUrl}/project/edit`, projectData);
  }

  getProjectInstructions(projectId: string, kind: ProjectInstructionKind, historyLimit = 50): Observable<IResponse<ProjectInstructionSectionRecord>> {
    const params = new HttpParams().set('history_limit', historyLimit.toString());
    return this._http
      .get<IResponse<ProjectInstructionSectionRecord>>(`${this.apiUrl}/project/${projectId}/instructions/${kind}`, { params })
      .pipe(this.retryWhenServiceUnavailable());
  }

  saveProjectInstructions(projectId: string, kind: ProjectInstructionKind, request: SaveProjectInstructionsRequest): Observable<IResponse<ProjectInstructionSectionRecord>> {
    return this._http.put<IResponse<ProjectInstructionSectionRecord>>(`${this.apiUrl}/project/${projectId}/instructions/${kind}`, request);
  }

  sendFeedback(feedback: FeedbackRequest): Observable<IResponse<Feedback>> {
    return this._http.post<IResponse<Feedback>>(`${this.apiUrl}/sessions/feedback`, feedback);
  }

  /**
   * Reads the stored thumbs up/down for a session's messages so the ratings survive a reload.
   * The backend returns only the latest rating per message, so at most one entry per message id.
   */
  getSessionFeedback(sessionId: string): Observable<IResponse<Feedback[]>> {
    return this._http.get<IResponse<Feedback[]>>(`${this.apiUrl}/sessions/${sessionId}/feedback`);
  }

  /**
   * DATA-1439: a 404 is the backend's single answer for "unknown message", "not your session" and
   * "nothing rated", so it always means the rating is already gone — the outcome the click asked for.
   * It is mapped to success here rather than left for each caller to special-case, and it is never retried.
   */
  deleteFeedback(messageId: string): Observable<IResponse<boolean>> {
    return this._http
      .delete<IResponse<boolean>>(`${this.apiUrl}/sessions/feedback/${messageId}`)
      .pipe(catchError(error => (error instanceof HttpErrorResponse && error.status === 404 ? of(AIAgentService.FEEDBACK_ALREADY_GONE) : throwError(() => error))));
  }
}
